import { useClient } from "../useClient";
import { useNotificationStore } from "@/stores/notification";
import { AppData, useAppData } from "../useAppData";

import { ratingMapper } from "@/api/mappers/RatingMapper";
import { Rating } from "@/api/models/Rating";
import { ClassSkillsRating } from "@/api/models/Classes/ClassSkillsRating";
import { ClassSkill } from "@/api/models/Classes/ClassSkill";
import { jpClassSkillsDTOMapper } from "@/api/mappers/ClassSkillMapper";
import { jpClassSkillsRatingsDTOMapper } from "@/api/mappers/ClassSkillsRatingMapper";
import { RatingsFullDTO } from "@/api/dto/RatingsFullDTO";
import { tagMapper } from "@/api/mappers/TagMapper";
import { personTypeMapper } from "@/api/mappers/PersonTypeMapper";
import { membershipTypeMapper } from "@/api/mappers/MembershipTypeMapper";
import { MembershipTypeDiscount } from "@/api/models/MembershipTypeDiscount";
import { MembershipType } from "@/api/models/MembershipType";
import { PersonType } from "@/api/models/Person/PersonType";
import { Tag } from "@/api/models/Person/Tag";
import { ProgramType } from "@/api/models/ProgramType";
import { BoatType } from "@/api/models/BoatType";
import { DonationFund } from "@/api/models/DonationFunds";
import { SwimProof } from "@/api/models/SwimProof";
import { swimProofMapper } from "@/api/mappers/SwimProofMapper";
import { donationFundsMapper } from "@/api/mappers/DonationFundsMapper";
import { boatMapper } from "@/api/mappers/BoatMapper";
import { programTypeMapper } from "@/api/mappers/ProgramTypeMapper";
import { membershipTypeDiscountMapper } from "@/api/mappers/MembershipTypeDiscountMapper";
import {
  ClassInstructor,
  classInstructorMapper,
} from "@/api/models/Classes/ClassInstructor";
import { ClassLocation } from "@/api/models/Classes/ClassLocation";
import { classLocationMapper } from "@/api/mappers/ClassLocationMapper";
import { UnifiedClassAssessments } from "@/api/models/UnifiedClassAssessments";
import { unifiedClassAssessmentsMapper } from "@/api/mappers/UnifiedClassAssessmentsMapper";
import { unifiedClassSkillsMapper } from "@/api/mappers/UnifiedClassSkillsMapper";
import { UnifiedClassSkills } from "@/api/models/UnifiedClassSkills";

/*
  /tags
  /personTypes
  /membershipTypes
  /membershipTypeDiscounts
  /programTypes
  /ratings
  /boatTypes
  /donationFunds
  /swimProofMethods
  /classInstructors
  /classLocations
  /jpClassSkills
  /jpClassSkillsRatings
  /countries
  /states
*/

export interface LookupsAPI {
  fetchRatings: () => Promise<Rating[] | undefined>;
  /**
   * @deprecated
   */
  fetchJpClassSkills: () => Promise<ClassSkill[] | undefined>;
  /**
   * @deprecated
   */
  fetchJpClassSkillsRatings: () => Promise<ClassSkillsRating[] | undefined>;
  fetchTags: () => Promise<Tag[]>;
  fetchPersonTypes: () => Promise<PersonType[]>;
  fetchMembershipTypes: () => Promise<MembershipType[]>;
  fetchMembershipTypeDiscounts: () => Promise<MembershipTypeDiscount[]>;
  fetchProgramTypes: () => Promise<ProgramType[]>;
  fetchBoatTypes: () => Promise<BoatType[]>;
  fetchDonationFunds: () => Promise<DonationFund[]>;
  fetchSwimProofMethods: () => Promise<SwimProof[]>;
  fetchClassInstructors: () => Promise<ClassInstructor[]>;
  fetchClassLocations: () => Promise<ClassLocation[]>;
  fetchUnifiedClassAssessments: () => Promise<
    UnifiedClassAssessments[] | undefined
  >;
  fetchUnifiedClassSkills: () => Promise<UnifiedClassSkills[] | undefined>;
}

let lookupsAPI: LookupsAPI | null = null;

export const useLookupsAPI = (): LookupsAPI => {
  if (!lookupsAPI) {
    // globals
    const client = useClient();
    const notificationStore = useNotificationStore();

    // functions
    const fetchRatings = async (): Promise<Rating[]> => {
      const { ratings: ratingsJson } = useAppData();

      try {
        const { data } = await client.get<RatingsFullDTO[]>("/staff/ratings");
        return data.map(ratingMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Ratings from database, defaulting to local data.",
          "warning",
        );

        // If the endpoint fails, use local JSON
        return ratingsJson;
      }
    };

    /**
     * @deprecated
     */
    const fetchJpClassSkills = async (): Promise<ClassSkill[] | undefined> => {
      try {
        const ret = await client.get("/staff/jpClassSkills");
        const activeJpClassSkills = ret.data.map(jpClassSkillsDTOMapper);
        return activeJpClassSkills;
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar("Failed to fetch JP Skills", "error");
      }
    };

    /**
     * @deprecated
     */
    const fetchJpClassSkillsRatings = async (): Promise<
      ClassSkillsRating[] | undefined
    > => {
      try {
        const { data } = await client.get("/staff/jpClassSkillsRatings");
        return data.map(jpClassSkillsRatingsDTOMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch JP Skill Ratings",
          "error",
        );
      }
    };

    const fetchUnifiedClassAssessments = async (): Promise<
      UnifiedClassAssessments[] | undefined
    > => {
      try {
        const { data } = await client.get("/staff/unifiedClassAssessments");

        return data.map(unifiedClassAssessmentsMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Class Assessments Ratings",
          "error",
        );
      }
    };

    const fetchUnifiedClassSkills = async (): Promise<
      UnifiedClassSkills[] | undefined
    > => {
      try {
        const { data } = await client.get("/staff/unifiedClassSkills");

        return data.map(unifiedClassSkillsMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Class Skills from database",
          "warning",
        );
      }
    };

    const fetchTags = async (): Promise<Tag[]> => {
      const { availableTags } = useAppData();

      try {
        const { data } = await client.get("/staff/tags");
        return data.map(tagMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Tags from database, defaulting to local data.",
          "warning",
        );
        return availableTags;
      }
    };

    const fetchPersonTypes = async (): Promise<PersonType[]> => {
      const { personTypes } = useAppData();

      try {
        const { data } = await client.get("/staff/personTypes");
        return data.map(personTypeMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Person Types from database, defaulting to local data.",
          "warning",
        );
        return personTypes;
      }
    };

    const fetchMembershipTypes = async (): Promise<MembershipType[]> => {
      const { membershipTypes } = useAppData();

      try {
        const { data } = await client.get("/staff/membershipTypes");
        return data.map(membershipTypeMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Membership Types from database, defaulting to local data.",
          "warning",
        );
        return membershipTypes;
      }
    };

    const fetchMembershipTypeDiscounts = async (): Promise<
      MembershipTypeDiscount[]
    > => {
      const { membershipTypeDiscounts } = useAppData();

      try {
        const { data } = await client.get("/staff/membershipTypeDiscounts");
        return data.map(membershipTypeDiscountMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Membership Type Discounts from database, defaulting to local data.",
          "warning",
        );
        return membershipTypeDiscounts;
      }
    };

    const fetchProgramTypes = async (): Promise<ProgramType[]> => {
      const { programTypes } = useAppData();

      try {
        const { data } = await client.get("/staff/programTypes");
        return data.map(programTypeMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Program Types from database, defaulting to local data.",
          "warning",
        );
        return programTypes;
      }
    };

    const fetchBoatTypes = async (): Promise<BoatType[]> => {
      const { boatTypes } = useAppData();

      try {
        const { data } = await client.get("/staff/boatTypes");
        return data.map(boatMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Boat Types from database, defaulting to local data.",
          "warning",
        );
        return boatTypes;
      }
    };

    const fetchDonationFunds = async (): Promise<DonationFund[]> => {
      const { donationFunds } = useAppData();

      try {
        const { data } = await client.get("/staff/donationFunds");
        return data.map(donationFundsMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Donation Funds from database, defaulting to local data.",
          "warning",
        );
        return donationFunds;
      }
    };

    const fetchSwimProofMethods = async (): Promise<SwimProof[]> => {
      const { swimProofMethods } = useAppData();

      try {
        const { data } = await client.get("/staff/swimProofMethods");
        return data.map(swimProofMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Swim Proof Methods from database, defaulting to local data.",
          "warning",
        );
        return swimProofMethods;
      }
    };

    const fetchClassInstructors = async (): Promise<ClassInstructor[]> => {
      const { classInstructors } = useAppData();

      try {
        const { data } = await client.get("/staff/classInstructors");
        return data.map(classInstructorMapper);
      } catch (error: any) {
        notificationStore.addSnackbar(
          "Failed to fetch Class Instructors from database, defaulting to local data.",
          "warning",
        );
        return classInstructors;
      }
    };

    const fetchClassLocations = async (): Promise<ClassLocation[]> => {
      const { classLocations } = useAppData();

      try {
        const { data } = await client.get("/staff/classLocations");
        return data.map(classLocationMapper);
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar(
          "Failed to fetch Class Locations from database, defaulting to local data.",
          "warning",
        );
        return classLocations;
      }
    };

    // const fetchLookup = async <dto, model>(key: string): Promise<model[]> => {
    //   const appData: AppData = useAppData();

    //   try {
    //     const { data } = await client.get<dto[]>("/staff/classLocations");
    //     return data.map(classLocationMapper);
    //   } catch (error: any) {
    //     console.log(error);
    //     notificationStore.addSnackbar(
    //       "Failed to fetch Class Locations from database, defaulting to local data.",
    //       "warning"
    //     );
    //     return appData[key];
    //   }
    // };

    // buidling object
    lookupsAPI = {
      fetchRatings,
      fetchJpClassSkills,
      fetchJpClassSkillsRatings,
      fetchTags,
      fetchPersonTypes,
      fetchMembershipTypes,
      fetchMembershipTypeDiscounts,
      fetchProgramTypes,
      fetchBoatTypes,
      fetchDonationFunds,
      fetchSwimProofMethods,
      fetchClassInstructors,
      fetchClassLocations,
      fetchUnifiedClassAssessments,
      fetchUnifiedClassSkills,
    };
  }

  return lookupsAPI;
};
